import * as vars from '@fullstory/proto/fs/api/vars';
import { SpecObjectMapping } from '@fullstory/proto/fs/services/search-settings';
import { SpecObjectInfo, useFsSpecActions } from '@fullstory/redux';
import { FieldScope } from '@fullstory/search-vars';
import { useCallback } from 'react';
import { PropertyRow } from '../types';

export const useUpdatePropertyMapping = (existingSpecObjects: SpecObjectInfo[]) => {
  const { batchUpdateSpecMappings } = useFsSpecActions();

  return useCallback(
    async (
      property: PropertyRow,
      existingSpecPropertySlugs: string[],
      updatedSpecPropertySlugs: string[],
    ) => {
      if (property.propertyType === FieldScope.NoScope) {
        return; // shouldn't be possible, nothing to do if it is
      }
      const updatedSlugSet = new Set(updatedSpecPropertySlugs);
      const removedSlugSet = new Set(
        existingSpecPropertySlugs.filter(slug => !updatedSlugSet.has(slug)),
      );

      const updatedSpecMappings: SpecObjectMapping[] = [];
      for (const existingSpecObject of existingSpecObjects) {
        const existingSpecObjectMapping = existingSpecObject.mapping;
        if (!existingSpecObjectMapping) {
          continue; // shouldn't hit this, need an object mapping to be able to add properties
        }

        const updatedProperties = existingSpecObjectMapping.properties.filter(
          prop => !removedSlugSet.has(prop.propertySlug),
        );
        const updatedPropertiesSlugSet = new Set(updatedProperties.map(prop => prop.propertySlug));
        // if we removed some properties, we need to update the mapping
        let updated = updatedProperties.length < existingSpecObjectMapping.properties.length;

        for (const specObjectProp of existingSpecObject.properties) {
          if (
            !updatedSlugSet.has(specObjectProp.slug) ||
            updatedPropertiesSlugSet.has(specObjectProp.slug)
          ) {
            continue; // either not requested in updated set or already in mapping
          }
          updatedProperties.push({
            propertySlug: specObjectProp.slug,
            targetPropertyScope: vars.Scope[property.propertyType],
            targetPropertyName: property.displayName,
            targetPropertyType: vars.Type[property.dataType],
          });
          updated = true; // we added a new property, so we need to update the mapping
        }
        if (updated) {
          updatedSpecMappings.push({
            ...existingSpecObjectMapping,
            properties: updatedProperties,
          });
        }
      }
      if (updatedSpecMappings.length) {
        await batchUpdateSpecMappings(updatedSpecMappings);
      }
    },
    [existingSpecObjects, batchUpdateSpecMappings],
  );
};
